/**
 * Server-held ICS-202 generation state: which boxes PRATUS AI is writing right
 * now (shared with every collaborator), the per-box result of a run, and the
 * family's draftable boxes.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import config from 'constants/apiConfig';
import type { IcsFormFamily } from 'components/IAP/formFamily';
import { aiFieldProvenanceKeys } from 'components/DisasterGPT/fieldDraft/useAiFieldProvenance';

import { keys as incidentInfoKeys } from '../hooks/incidentInfoHooks';
import { keys as incidentObjectiveKeys } from '../hooks/incidentObjectiveHooks';
import { keys as objectiveKeys } from '../hooks/objectiveHooks';
import {
  ICS_202_BASE_ID,
  listIcs202FamilyFields,
  type Ics202FamilyField,
} from './ics202DraftPrompt';

export type Ics202AiLock = {
  field: string;
  runId: string;
  userGuid: string;
  acquiredAt: string;
  expiresAt: string;
};

export type Ics202FieldStatus = 'queued' | 'running' | 'done' | 'failed';

export type Ics202FieldResult = {
  status: Ics202FieldStatus;
  error?: string;
  citationCount?: number;
};

export type Ics202Run = {
  id: string;
  incidentId: string;
  periodNumber: number;
  status: 'running' | 'completed' | 'partial' | 'failed';
  fields: string[];
  results: Record<string, Ics202FieldResult>;
  summary: string | null;
};

export const FAST_LOCK_POLL_MS = 1500;
export const IDLE_LOCK_POLL_MS = 15000;

export const ics202Keys = {
  all: ['ics202'] as const,
  locks: (incidentId: string, period: number) =>
    [...ics202Keys.all, 'locks', incidentId, period] as const,
  run: (runId: string) => [...ics202Keys.all, 'run', runId] as const,
  fields: (family: IcsFormFamily) =>
    [...ics202Keys.all, 'fields', family] as const,
};

/**
 * Poll fast while any box is locked (or a run was just started), slowly
 * otherwise.
 */
export const lockPollInterval = (
  lockCount: number,
  expectingLocks: boolean
): number =>
  lockCount > 0 || expectingLocks ? FAST_LOCK_POLL_MS : IDLE_LOCK_POLL_MS;

/** Boxes that were locked on the previous poll and are not any more. */
export const releasedFields = (
  previous: ReadonlySet<string>,
  next: ReadonlySet<string>
): string[] => [...previous].filter((field) => !next.has(field));

/**
 * Refetch what a just-written box changed: the incident-info record, the
 * objectives list (box 6 / FEMA box 3), the citation chips and the 202 version
 * chain.
 */
export const invalidateIcs202Boxes = (
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  incidentId: string,
  fields: readonly string[]
): void => {
  if (fields.length === 0) return;
  void queryClient.invalidateQueries({
    queryKey: incidentInfoKeys.get(incidentId as UUID),
  });
  if (fields.includes('objectives')) {
    void queryClient.invalidateQueries({
      queryKey: incidentObjectiveKeys.all,
    });
    void queryClient.invalidateQueries({ queryKey: objectiveKeys.all });
  }
  void queryClient.invalidateQueries({
    queryKey: aiFieldProvenanceKeys.byTarget('incident_info', incidentId),
  });
  void queryClient.invalidateQueries({
    queryKey: ['icsFormTip', ICS_202_BASE_ID],
  });
  void queryClient.invalidateQueries({ queryKey: ['icsFormTipsForPeriod'] });
  void queryClient.invalidateQueries({
    queryKey: ['icsFormVersions', ICS_202_BASE_ID],
  });
};

export const useIcs202AiLocks = (
  incidentId: string | undefined,
  periodNumber: number,
  expectingLocks: boolean
) =>
  useQuery({
    queryKey: ics202Keys.locks(incidentId ?? '', periodNumber),
    queryFn: async () => {
      const { data } = await axios.get<{ locks: Ics202AiLock[] }>(
        config.apiGateway.ics202Locks,
        { params: { incidentId, period: periodNumber } }
      );
      return data.locks ?? [];
    },
    enabled: Boolean(incidentId),
    refetchInterval: (query) =>
      lockPollInterval(query.state.data?.length ?? 0, expectingLocks),
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
  });

export const useIcs202Run = (
  runId: string | null,
  incidentId: string | undefined
) =>
  useQuery({
    queryKey: ics202Keys.run(runId ?? ''),
    queryFn: async () => {
      const { data } = await axios.get<Ics202Run>(
        `${config.apiGateway.ics202Runs}/${runId}`,
        { params: { incidentId } }
      );
      return data;
    },
    enabled: Boolean(runId && incidentId),
    refetchInterval: (query) =>
      query.state.data && query.state.data.status !== 'running'
        ? false
        : FAST_LOCK_POLL_MS * 2,
    retry: false,
  });

/** The family's draftable boxes from the server, seeded by the client mirror. */
export const useIcs202FamilyFields = (family: IcsFormFamily) =>
  useQuery({
    queryKey: ics202Keys.fields(family),
    queryFn: async (): Promise<Ics202FamilyField[]> => {
      const { data } = await axios.get<{ fields: Ics202FamilyField[] }>(
        config.apiGateway.ics202Fields,
        { params: { family } }
      );
      return data.fields?.length
        ? data.fields.map(({ source, label }) => ({ source, label }))
        : listIcs202FamilyFields(family);
    },
    initialData: () => listIcs202FamilyFields(family),
    initialDataUpdatedAt: 0,
    staleTime: Infinity,
  });
